import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Phone, ArrowUpRight, Star, ShieldCheck, Wrench, Wind, Home as HomeIcon, SearchCheck, Waves, Clock } from "lucide-react";
import { COMPANY, AREAS, SERVICES, TESTIMONIALS, CHAPTERS, IMAGES, BBB, HOURS } from "@/content";
import CallLink from "@/components/CallLink";
import { Reveal, MaskedLines } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { usePageMeta } from "@/hooks/usePageMeta";

const ICONS = { maintenance: Wrench, repair: Wind, inspections: SearchCheck, "duct-cleaning": Waves, "indoor-air-quality": HomeIcon };

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-start overflow-hidden bg-charcoal">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="The Juli Klein's A/C Services team in front of their service van" className="h-full w-full object-cover object-[center_68%]" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/90 via-charcoal/60 via-[40%] to-transparent to-[66%]" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl w-full px-5 md:px-10 pt-28 md:pt-32 pb-[52vh] md:pb-[46vh]">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-terracotta text-xs md:text-sm tracking-[0.3em] uppercase font-semibold mb-4 drop-shadow">
          Fort Myers · Estero · Naples · Bonita Springs
        </motion.p>
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 lg:gap-12 lg:items-end">
          <h1 className="font-heading text-bone font-medium tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl [text-shadow:0_2px_24px_rgba(0,0,0,0.55)]">
            <MaskedLines lines={["Veteran-Owned &", "Trusted A/C Services in", "Southwest Florida."]} />
          </h1>
          <div>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
              className="hidden sm:block max-w-md text-bone/90 text-base md:text-lg leading-relaxed [text-shadow:0_1px_12px_rgba(0,0,0,0.6)]">
              {COMPANY.full} is your honest, family-run choice for A/C repair, maintenance and installation across Southwest Florida. Solutions — not sales pitches.
            </motion.p>
            <motion.a href="#contact" data-testid="hero-discount-badge" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.6 }}
              className="group mt-4 sm:mt-5 inline-flex items-center gap-3 rounded-full border-2 border-terracotta/80 bg-charcoal/60 backdrop-blur pl-2 pr-5 py-2 text-bone shadow-xl shadow-charcoal/50 hover:border-terracotta hover:bg-charcoal/75 hover:-translate-y-0.5 transition-all duration-200">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-3 md:px-3.5 py-1.5 text-sm md:text-base font-bold tracking-wide whitespace-nowrap">
                <ShieldCheck size={17} /> 10% OFF
              </span>
              <span className="text-[13px] sm:text-sm md:text-lg font-semibold whitespace-nowrap">Veterans &amp; First Responders</span>
              <span className="relative flex h-2.5 w-2.5 ml-0.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-terracotta opacity-75 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-terracotta" />
              </span>
            </motion.a>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-4 sm:mt-5 flex flex-col sm:flex-row gap-3">
              <a href="#contact" data-testid="hero-request-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-7 py-3.5 font-semibold text-bone shadow-lg shadow-charcoal/40 hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-200">
                Request Service <ArrowUpRight size={18} />
              </a>
              <CallLink data-testid="hero-call-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-bone/50 bg-charcoal/40 backdrop-blur px-7 py-3.5 font-semibold text-bone hover:bg-charcoal/60 transition-colors duration-200">
                <Phone size={17} /> {COMPANY.phone}
              </CallLink>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AreaMarquee() {
  const items = [...AREAS, "Year-Round Service", ...AREAS, "All Makes & Models"];
  return (
    <div data-testid="area-marquee" className="bg-sand py-6 border-y border-sand">
      <Marquee speed={38} gradient={false} autoFill>
        {items.map((a, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8 font-heading text-3xl md:text-4xl text-sage">
            {a} <Star size={16} className="text-terracotta" fill="#C0583F" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Manifesto() {
  return (
    <section className="mx-auto max-w-7xl px-5 md:px-10 py-24 md:py-36">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-terracotta text-xs tracking-[0.25em] uppercase font-semibold mb-5">Why neighbors trust us</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-charcoal">
              A different approach to every service call.
            </h2>
            <p className="mt-6 text-ink leading-relaxed max-w-md">
              With so many commission-driven companies in the area, it's hard to know who to trust. We built {COMPANY.full} to be the opposite.
            </p>
            <Link to="/services" data-testid="manifesto-services-link" className="mt-8 inline-flex items-center gap-2 text-terracotta font-semibold hover:gap-3 transition-all duration-200">
              Explore our services <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal>
            <div className="relative mb-12 overflow-hidden rounded-3xl">
              <img src="/why-trust.webp" alt="Juli Klein's technician servicing an air handler on site" className="w-full h-64 md:h-80 object-cover" />
              <div className="pointer-events-none absolute inset-0 rounded-3xl shadow-[inset_0_0_55px_22px_#F8F7F3]" />
              <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-t from-bone/40 via-transparent to-transparent" />
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-12">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="border-t border-sand pt-5">
                  <span className="font-heading text-5xl text-terracotta/30">{c.n}</span>
                  <h3 className="font-heading text-2xl mt-2 text-charcoal">{c.title}</h3>
                  <p className="mt-2 text-ink leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="bg-sand/50 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <Reveal>
            <p className="text-terracotta text-xs tracking-[0.25em] uppercase font-semibold mb-4">Residential & Commercial</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-charcoal max-w-2xl leading-[0.98]">Everything your air conditioning needs.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/services" data-testid="services-preview-link" className="inline-flex items-center gap-2 rounded-full border border-charcoal/20 px-6 py-3 font-semibold text-charcoal hover:bg-charcoal hover:text-bone transition-colors duration-200 whitespace-nowrap">
              View all services <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
        <div data-testid="services-preview-grid" className="flex flex-wrap justify-center gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.slug] || ShieldCheck;
            return (
              <Reveal key={s.slug} delay={(i % 3) * 0.08} className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
                <Link to={`/services/${s.path}`} data-testid={`service-card-${s.slug}`} className="group block h-full rounded-2xl bg-white border border-sand overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                  <div className="relative h-52 overflow-hidden">
                    <img src={s.image} alt={s.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute top-4 left-4 grid place-items-center h-11 w-11 rounded-full bg-bone/90 text-terracotta backdrop-blur"><Icon size={20} /></span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-2xl text-charcoal">{s.title}</h3>
                    <p className="mt-2 text-ink leading-relaxed text-sm">{s.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-terracotta font-semibold text-sm group-hover:gap-2.5 transition-all duration-200">Learn more <ArrowUpRight size={15} /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServiceArea() {
  return (
    <section className="mx-auto max-w-7xl px-5 md:px-10 py-24 md:py-32">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="relative">
            <img src={IMAGES.area} alt="Southwest Florida coastal community" className="rounded-3xl w-full h-[420px] md:h-[520px] object-cover" />
            <div className="absolute -bottom-6 -right-4 md:right-8 bg-terracotta text-bone rounded-2xl px-7 py-5 shadow-lg">
              <p className="font-heading text-4xl leading-none">365</p>
              <p className="text-xs uppercase tracking-widest mt-1 text-bone/80">days a year</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-terracotta text-xs tracking-[0.25em] uppercase font-semibold mb-5">Serving Southwest Florida</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-charcoal leading-[0.98]">Year-round comfort for our community.</h2>
          <p className="mt-6 text-ink leading-relaxed max-w-lg">
            Florida heat doesn't take a season off — and neither do we. We proudly repair and service all air conditioning makes and models for homes and businesses throughout:
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
            {AREAS.map((a) => (
              <div key={a} className="flex items-center gap-3 rounded-xl bg-sand/60 px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-terracotta" />
                <span className="font-medium text-charcoal">{a}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section id="reviews" data-testid="reviews-section" className="bg-charcoal text-bone py-24 md:py-32 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal>
          <p className="text-terracotta text-xs tracking-[0.25em] uppercase font-semibold mb-4">5-star Google reviews</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-2xl leading-[0.98]">Loved by neighbors across the coast.</h2>
        </Reveal>
        <Reveal delay={0.08}>
          <a href={BBB.profileUrl} target="_blank" rel="noopener noreferrer" data-testid="reviews-bbb-badge"
            className="mt-8 inline-flex items-center gap-4 rounded-2xl bg-white p-3 pr-6 hover:-translate-y-0.5 transition-transform duration-200">
            <img src={BBB.seal} alt="BBB Accredited Business" className="h-14 w-auto" />
            <span className="leading-tight text-left">
              <span className="block font-heading text-2xl text-sage">{BBB.rating} Rated · BBB Accredited</span>
              <span className="block text-[11px] uppercase tracking-widest text-ink">Accredited since {BBB.accreditedSince} · Lic. {BBB.license}</span>
            </span>
          </a>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <figure className="h-full rounded-2xl bg-bone/5 border border-bone/10 p-7 backdrop-blur">
                <div className="flex gap-1 mb-4">{Array.from({ length: 5 }).map((_, j) => <Star key={j} size={16} className="text-terracotta" fill="#C0583F" />)}</div>
                <blockquote className="text-bone/85 leading-relaxed text-[15px]">"{t.quote}"</blockquote>
                <figcaption className="mt-5 font-heading text-xl">{t.author} <span className="block text-xs font-body tracking-widest uppercase text-bone/40 mt-1">{t.source}</span></figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-7xl px-5 md:px-10 py-24 md:py-32">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <Reveal>
          <p className="text-terracotta text-xs tracking-[0.25em] uppercase font-semibold mb-5">Get a free quote</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-charcoal leading-[0.98]">Let's get your air right.</h2>
          <p className="mt-6 text-ink leading-relaxed max-w-md">
            Tell us what's going on and we'll follow up with an honest recommendation and a free quote. Prefer to talk? Call us directly.
          </p>
          <CallLink className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-sage text-bone px-6 py-5 hover:bg-sage/90 transition-colors duration-200">
            <span className="grid place-items-center h-11 w-11 rounded-full bg-bone/15"><Phone size={20} /></span>
            <span><span className="block text-xs uppercase tracking-widest text-bone/70">Call us today</span><span className="font-heading text-2xl">{COMPANY.phone}</span></span>
          </CallLink>
          <div data-testid="business-hours" className="mt-4 flex w-fit items-center gap-2.5 rounded-full border border-sand bg-white px-4 py-2 text-sm text-charcoal">
            <Clock size={15} className="text-terracotta" />
            <span><span className="font-semibold">{HOURS.days}</span> · {HOURS.time}</span>
          </div>
        </Reveal>
        <Reveal delay={0.1}><ContactForm /></Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta(null, "Veteran-owned, family-run A/C repair, maintenance, installation, inspections, duct & dryer vent cleaning and indoor air quality. Serving Fort Myers, Estero, Naples & Bonita Springs, FL year-round. Call (239) 671-4867.");
  return (
    <>
      <Hero />
      <AreaMarquee />
      <Manifesto />
      <ServicesPreview />
      <ServiceArea />
      <Testimonials />
      <Contact />
    </>
  );
}
